import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const PopupForm = ({ onClose }) => {
  const overlayRef = useRef(null);
  const firstFieldRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Lock background scroll
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevPr = document.body.style.paddingRight;
    const scrollBarGap = window.innerWidth - document.documentElement.clientWidth;
    if (scrollBarGap > 0) document.body.style.paddingRight = `${scrollBarGap}px`;
    document.body.style.overflow = "hidden";

    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);

    firstFieldRef.current?.focus({ preventScroll: true });

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPr;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const handleBackdrop = (e) => {
    if (e.target === overlayRef.current) onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.target);
    formData.append("form_type", "popup");

    try {
      const response = await fetch("/backend/submit.php", {
        method: "POST",
        body: formData,
      });

      if (response.redirected) {
        window.location.href = response.url;
      } else {
        alert("Submission failed. Please try again.");
        setIsSubmitting(false);
      }
    } catch (err) {
      alert("An error occurred while submitting the form.");
      setIsSubmitting(false);
    }
  };

  const modal = (
    <div
      ref={overlayRef}
      onMouseDown={handleBackdrop}
      className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4 sm:p-6"
      style={{ minHeight: "100dvh", WebkitOverflowScrolling: "touch" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="popup-title"
    >
      <div
        className="bg-white w-full max-w-lg rounded-2xl shadow-xl relative p-5 sm:p-6
                   max-h-[90dvh] overflow-y-auto
                   pt-[max(env(safe-area-inset-top),1rem)]
                   pb-[max(env(safe-area-inset-bottom),1rem)]"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-2.5 right-3 text-gray-600 hover:text-red-600 text-2xl leading-none"
          aria-label="Close dialog"
          type="button"
        >
          &times;
        </button>

        <h2 id="popup-title" className="text-2xl font-semibold mb-6 text-[#741112] text-center">
          Get in Touch
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            ref={firstFieldRef}
            type="text"
            name="name"
            placeholder="Name"
            required
            className="w-full border border-gray-300 rounded px-4 py-3 text-[16px]
                       focus:outline-none focus:ring-2 focus:ring-[#741112]/40"
          />

          <input
            type="email"
            name="email"
            placeholder="Email ID"
            required
            className="w-full border border-gray-300 rounded px-4 py-3 text-[16px]
                       focus:outline-none focus:ring-2 focus:ring-[#741112]/40"
          />

          <input
            type="tel"
            name="phone"
            placeholder="Phone Number"
            required
            className="w-full border border-gray-300 rounded px-4 py-3 text-[16px]
                       focus:outline-none focus:ring-2 focus:ring-[#741112]/40"
          />

          <select
            name="type"
            required
            className="w-full border border-gray-300 rounded px-4 py-3 text-[16px] text-gray-700
                       focus:outline-none focus:ring-2 focus:ring-[#741112]/40"
            defaultValue=""
          >
            <option value="" disabled>Developer / Channel Partner</option>
            <option value="Developer">Developer</option>
            <option value="Channel Partner">Channel Partner</option>
          </select>

          <input
            type="text"
            name="location"
            placeholder="Location"
            required
            className="w-full border border-gray-300 rounded px-4 py-3 text-[16px]
                       focus:outline-none focus:ring-2 focus:ring-[#741112]/40"
          />

          <input
            type="text"
            name="project"
            placeholder="Project Name"
            required
            className="w-full border border-gray-300 rounded px-4 py-3 text-[16px]
                       focus:outline-none focus:ring-2 focus:ring-[#741112]/40"
          />

          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full flex items-center justify-center gap-2 bg-[#741112] text-white py-3 rounded-md font-semibold transition ${
              isSubmitting ? "opacity-60 cursor-not-allowed" : "hover:bg-[#600e10]"
            }`}
          >
            {isSubmitting && (
              <svg
                className="animate-spin h-5 w-5 text-white"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                />
              </svg>
            )}
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </form>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(modal, document.body) : modal;
};

export default PopupForm;
